import { getErrorMessage } from '../utils/serviceUtils';
import React from 'react';
import type { AuthChangeEvent, AuthSession } from '@supabase/supabase-js';
import { supabase } from '../utils/supabaseClient';
import { clearCachedMyProfile } from '../services/ProfileService';
import type { AuthProvider } from '../utils/auth';
import { buildAuthCallbackUrl } from '../utils/auth';
import { logger } from '../utils/logger';
import { AuthContext, type AuthContextValue } from './authContextShared';

function extractRedirectUrl(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const candidate = (data as { url?: unknown }).url;
  return typeof candidate === 'string' && candidate.length > 0 ? candidate : null;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [initialized, setInitialized] = React.useState(false);
  const [session, setSession] = React.useState<AuthSession | null>(null);

  React.useEffect(() => {
    let active = true;

    const applySession = (nextSession: AuthSession | null) => {
      if (!active) return;
      setSession(nextSession);
      setInitialized(true);
    };

    void supabase.auth.getSession()
      .then(({ data, error }) => {
        if (error) {
          logger.warn('AUTH_GET_SESSION_FAILED', { message: error.message });
        }
        applySession(data.session ?? null);
      })
      .catch((error) => {
        logger.warn('AUTH_GET_SESSION_EXCEPTION', {
          message: getErrorMessage(error),
        });
        applySession(null);
      });

    const { data } = supabase.auth.onAuthStateChange((event: AuthChangeEvent, nextSession: AuthSession | null) => {
      logger.info('AUTH_STATE_CHANGED', {
        event,
        hasSession: Boolean(nextSession),
        userId: nextSession?.user?.id ?? null,
      });
      applySession(nextSession);
    });

    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, []);

  const signInWithProvider = React.useCallback(async (provider: AuthProvider, nextPath?: string) => {
    const redirectTo = buildAuthCallbackUrl(nextPath);
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo,
        skipBrowserRedirect: true,
      },
    });

    if (error) {
      logger.error('AUTH_SIGN_IN_FAILED', {
        provider,
        redirectTo,
        message: error.message,
      });
      throw error;
    }

    const targetUrl = extractRedirectUrl(data);
    if (!targetUrl) {
      throw new Error('Missing OAuth redirect URL');
    }

    window.location.assign(targetUrl);
  }, []);

  const signOut = React.useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      logger.error('AUTH_SIGN_OUT_FAILED', { message: error.message });
      throw error;
    }

    clearCachedMyProfile();
  }, []);

  const value = React.useMemo<AuthContextValue>(() => ({
    initialized,
    isAuthenticated: Boolean(session?.user),
    session,
    user: session?.user ?? null,
    signInWithProvider,
    signOut,
  }), [initialized, session, signInWithProvider, signOut]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
